import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const FIELD_LABELS = {
  hero_intro: "Homepage hero introduction",
  newsletter_heading: "Newsletter section heading",
  newsletter_copy: "Newsletter section copy",
  discovery_teaser_heading: "Homepage Wellness Discovery teaser heading",
  consult_terrain_blurb: "Terrain & Truth card description",
  consult_full_blurb: "Full 90-Minute card description",
  consult_note: "Note below the consultation cards",
  booking_url: "Consultation booking link (used by every Book button and report)",
  footer_email: "Footer contact email line",
  hero_cta_primary: "Primary booking button text",
};

export default function AdminContent() {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Website Content | Rooted Healing Admin";
    api.get("/admin/content").then(({ data }) => setValues(data.values));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { values });
      toast.success("Content saved. The site shows it immediately.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  if (!values) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div data-testid="admin-content">
      <h1 className="font-serif text-3xl text-forest">Website Content</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-taupe">
        Simple editing controls for key marketing copy. The design stays untouched; these
        fields update text only. Assessment questions and scoring are intentionally not
        editable here.
      </p>
      <div className="mt-8 flex max-w-3xl flex-col gap-6">
        {Object.keys(FIELD_LABELS).map((key) => (
          <div key={key}>
            <label
              htmlFor={`content-${key}`}
              className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-forest"
            >
              {FIELD_LABELS[key]}
            </label>
            <textarea
              id={`content-${key}`}
              data-testid={`content-field-${key}`}
              rows={key.includes("heading") ? 2 : 4}
              value={values[key] || ""}
              onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
              className="w-full rounded-2xl border border-forest/20 bg-white/70 px-5 py-3.5 text-sm leading-relaxed text-charcoal outline-none focus:border-gold"
            />
          </div>
        ))}
        <div>
          <button
            data-testid="content-save-button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 text-sm font-semibold text-ivory transition-colors hover:bg-forest-deep disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Content
          </button>
        </div>
      </div>
    </div>
  );
}
